import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { FONT_FAMILY } from "../../config/themeConstants";
import { API_BASE_URL } from "../../config/constants";
import { GlobalContext } from "../../GlobalProvider";

const MAX_CHECK_DURATION = 30000; // 1 Minute
const CHECK_INTERVAL = 5000; // 5 seconds

const ConfirmationPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const orderNo = queryParams.get("orderNo");
  const isBuyNow = queryParams.get("buynow") === "true";

  const { globalVariable } = useContext(GlobalContext);
  const [transactionId, setTransactionId] = useState(orderNo);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checking, setChecking] = useState(true);

  const transId = new URLSearchParams(location.search).get("transactionId");


  useEffect(() => {
    if (!isBuyNow) {
      globalVariable.cartItems = {};
      globalVariable.comboCart = {};
      localStorage.removeItem("cartItems");
      localStorage.removeItem("comboCart");
    }

    if (transId) {
      monitorSalesStatus(transId);
    }
  }, [isBuyNow, transId, user]);

  const monitorSalesStatus = (upiTransactionId) => {
    let startTime = Date.now();

    const checkStatus = async () => {
      if (Date.now() - startTime >= MAX_CHECK_DURATION) {
        console.log("⌛ 1 minute passed, checking payment status...");
        setChecking(false);
        checkPaymentStatus(upiTransactionId);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/sales-status/${upiTransactionId}`
        );
        console.log("📌 Sales Status Response:", response.data);

        if (response.data.salesStatus) {
          setPaymentStatus(response.data.salesStatus);
          const status = response.data.salesStatus.toUpperCase();

          if (status === "SC") {
            console.log("✅ Sales Completed or Failed, stopping checks.");
            setPaymentStatus("SUCCESS");
            setTransactionId(upiTransactionId);
            setLoading(false);
            setChecking(false);
            return;
          } else if (status === "SF") {
            console.log("✅ Sales Completed or Failed, stopping checks.");
            setPaymentStatus("FAILED");
            setLoading(false);
            setChecking(false);
            return;
          }
        }
      } catch (error) {
        console.error(
          "❌ Error fetching sales status:",
          error.response?.data || error.message
        );
      }

      if (checking) {
        setTimeout(checkStatus, CHECK_INTERVAL);
      }
    };

    checkStatus();
  };

  const checkPaymentStatus = async (orderId) => {
    try {
      setLoading(true);

      const accessToken = localStorage.getItem("phonepe_auth_token");
      if (!accessToken) {
        console.error("❌ Access token not found.");
        setPaymentStatus("FAILED");
        setLoading(false);
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `O-Bearer ${accessToken}`,
      };

      const response = await axios.get(
        `${API_BASE_URL}/check-payment-status?merchantOrderId=${orderId}`,
        { headers }
      );

      console.log("🔍 PhonePe Response:", response.data);

      const state = response.data?.state || response.data?.data?.state;
      console.log("🔍 Extracted State:", state);

      if (state?.trim().toUpperCase() === "COMPLETED") {
        setPaymentStatus("SUCCESS");
        setTransactionId(transId);
      } else {
        setPaymentStatus("FAILED");
      }
    } catch (error) {
      console.error(
        "❌ Error checking payment status:",
        error.response?.data || error.message
      );
      setPaymentStatus("FAILED");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueShopping = () => {
    navigate(`/jewels/list/All`);
  };

  return (
    <Card
      elevation={4}
      sx={{
        maxWidth: "100%",
        margin: "auto",
        mt: 4,
        borderRadius: 4,
        backgroundColor: "#fff",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
        textAlign: "left",
        padding: 3,
      }}
    >
      <CardContent>
        <Typography
          gutterBottom
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            color: paymentStatus === "SUCCESS" ? "green" : "red",
            fontSize: "25px",
            textAlign: "center",
          }}
        >
          {loading
            ? "We are checking the status of your order. Please wait..."
            : paymentStatus === "SUCCESS"
            ? "Order Confirmed!"
            : "Payment Failed"}
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <CircularProgress size={50} />
            <Typography
              variant="body2"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: 16,
                color: "#555",
                mt: 2,
              }}
            >
              Please do not refresh or click the back button. This may take a
              few moments. We will update you shortly.
            </Typography>
          </Box>
        ) : paymentStatus === "SUCCESS" ? (
          <Typography
            variant="body1"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 18,
              color: "#333",
              textAlign: "left",
            }}
          >
            <span style={{ color: "green", fontWeight: "bold" }}>
              🎉 Congratulations!
            </span>{" "}
            Your order (<strong>{transId}</strong>) has been successfully
            placed.
            <br />A confirmation email has been sent to the address you
            provided. Our team is now preparing your order for dispatch. <br />
            Thank you for shopping with <strong>ILLOLAM</strong> — we appreciate
            your trust and look forward to delighting you.
          </Typography>
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 18,
              color: "#D32F2F",
              textAlign: "center",
              mt: 2,
            }}
          >
            Your payment could not be processed. Please try again.
          </Typography>
        )}

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Button
            onClick={handleContinueShopping}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 20,
              paddingX: 4,
              textTransform: "capitalize",
              fontWeight: 500,
            }}
          >
            Continue Shopping
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConfirmationPage;
